import { Voice } from './Voice';

/**
 * Tracks short-lived voices (one-shots fired via {@link AudioEngine.playOneShot}).
 * Enforces per-clip and global concurrency caps by stealing the oldest voice
 * when over budget.
 *
 * Long-lived `AudioSource`s (loops, music beds) are owner-managed and live
 * outside the pool — see `AudioEngine.sources`. They never get stolen on
 * voice pressure and they don't count toward `globalCap`.
 */
export class VoicePool {
  private readonly voices: Voice[] = [];
  private readonly perClip = new Map<string, number>();

  constructor(private globalCap: number) {}

  setGlobalCap(cap: number): void {
    this.globalCap = Math.max(1, cap | 0);
  }

  /**
   * Reserves a slot, stealing the oldest voice of the same clip / oldest voice
   * overall if needed. Returns false only if both caps are positive and pool
   * is empty — which is impossible in practice; kept for symmetry with future
   * priority-aware policies.
   */
  reserve(clipId: string, perClipCap: number): boolean {
    if (this.countForClip(clipId) >= perClipCap) {
      if (!this.stealOldestForClip(clipId)) return false;
    }
    if (this.voices.length >= this.globalCap) {
      if (!this.stealOldestAny()) return false;
    }
    return true;
  }

  /** Add a voice that has already been created and started. */
  track(voice: Voice): void {
    this.voices.push(voice);
    this.perClip.set(voice.clipId, (this.perClip.get(voice.clipId) ?? 0) + 1);
    voice.onEnded(() => this.untrack(voice));
  }

  /** Number of tracked voices. Tests and diagnostics. */
  get size(): number {
    return this.voices.length;
  }

  countForClip(clipId: string): number {
    return this.perClip.get(clipId) ?? 0;
  }

  /** Hard-stops every tracked voice. Called on scene deactivate / destroy. */
  stopAll(): void {
    // copy because stop() → onEnded → untrack mutates the array
    const snapshot = this.voices.slice();
    for (const v of snapshot) v.stop();
  }

  private untrack(voice: Voice): void {
    const idx = this.voices.indexOf(voice);
    if (idx >= 0) {
      this.voices[idx] = this.voices[this.voices.length - 1];
      this.voices.pop();
    }
    const n = (this.perClip.get(voice.clipId) ?? 0) - 1;
    if (n <= 0) this.perClip.delete(voice.clipId);
    else this.perClip.set(voice.clipId, n);
  }

  private stealOldestForClip(clipId: string): boolean {
    let oldest: Voice | null = null;
    for (const v of this.voices) {
      if (v.clipId !== clipId) continue;
      if (!oldest || v.startedAt < oldest.startedAt) oldest = v;
    }
    if (!oldest) return false;
    oldest.stop();
    return true;
  }

  private stealOldestAny(): boolean {
    let oldest: Voice | null = null;
    for (const v of this.voices) {
      if (!oldest || v.startedAt < oldest.startedAt) oldest = v;
    }
    if (!oldest) return false;
    oldest.stop();
    return true;
  }
}
